// StudentTestPage.jsx - with improved error handling and user feedback

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateTest, submitTest } from '../services/testService';
import TestQuestion from '../components/TestQuestion';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const StudentTestPage = () => {
  const { studentId, testNumber } = useParams();
  const navigate = useNavigate();
  
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  
  // For fallback test when server is unavailable
  const [usingSampleTest, setUsingSampleTest] = useState(false);
  
  // Log params for debugging
  console.log('Test ID from URL: ', `${studentId}-${testNumber}`);
  console.log('Student ID from URL: ', studentId);
  console.log('Test Number from URL: ', testNumber);

  useEffect(() => {
    // Ensure we have necessary params
    if (!studentId || !testNumber) {
      setError('Missing required parameters');
      setLoading(false);
      return;
    }

    fetchTest();
  }, [studentId, testNumber]);

  const fetchTest = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Fetching test with testNumber: ${testNumber} and studentId: ${studentId}`);
      const testData = await generateTest(studentId, parseInt(testNumber, 10));
      
      setTest(testData);
      
      // Initialize answers object with empty values for each question
      if (testData && testData.questions) {
        const initialAnswers = {};
        testData.questions.forEach(q => {
          initialAnswers[q.id] = '';
        });
        setAnswers(initialAnswers);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching test:', error);
      setError(error.message || 'Failed to load test');
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const result = await submitTest(studentId, parseInt(testNumber, 10), answers);
      setSubmitted(true);
      setLoading(false);
      
      // Navigate to results page or show results
      navigate(`/student/${studentId}/results/${testNumber}`);
    } catch (error) {
      setError(error.message || 'Failed to submit test');
      setLoading(false);
    }
  };
  
  const handleUseSampleTest = () => {
    // Provide a sample test for offline/development use
    setUsingSampleTest(true);
    setTest({
      id: `${studentId}-${testNumber}`,
      title: "Sample Test",
      description: "This is a sample test for offline/development use",
      questions: [
        {
          id: "q1",
          text: "What is 2 + 2?",
          type: "multiple_choice",
          options: ["3", "4", "5", "6"]
        },
        {
          id: "q2",
          text: "Explain the concept of gravity in your own words.",
          type: "essay"
        }
      ]
    });
    
    // Initialize answers
    setAnswers({
      q1: '',
      q2: ''
    });
    
    setLoading(false);
    setError(null);
  };
  
  const handleTryAgain = () => {
    fetchTest();
  };

  if (loading) {
    return <LoadingSpinner message="Loading test..." />;
  }

  if (error) {
    return (
      <div className="error-container">
        <ErrorMessage title="Error Loading Test" message={error} />
        <div className="error-actions">
          <button 
            className="btn btn-primary" 
            onClick={handleTryAgain}
          >
            Try Again
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleUseSampleTest}
          >
            Use Sample Test
          </button>
        </div>
      </div>
    );
  }

  if (!test) {
    return <div>No test found</div>;
  }

  if (submitted) {
    return <div>Test submitted successfully! Redirecting to results...</div>;
  }

  return (
    <div className="test-container">
      <div className="test-header">
        <h1>{test.title} {usingSampleTest && "(Sample)"}</h1>
        <p>{test.description}</p>
        {usingSampleTest && (
          <div className="alert alert-warning">
            You are using a sample test. Your answers will not be saved.
          </div>
        )}
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="questions-container">
          {test.questions.map((question, index) => (
            <TestQuestion
              key={question.id}
              questionNumber={index + 1}
              question={question}
              value={answers[question.id] || ''}
              onChange={(value) => handleAnswerChange(question.id, value)}
            />
          ))}
        </div>
        
        <div className="test-actions">
          <button 
            type="submit" 
            className="btn btn-primary submit-button"
            disabled={usingSampleTest}
          >
            Submit Test
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentTestPage;